// src/features/dashboard/superadmin/CreateSpreadsheetButton.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../hooks/useAuth';

// Define headers for each sheet
const SHEET_HEADERS = {
  Users: [
    'UserId', 'OrganisationId', 'Name', 'Surname', 'Email', 'Role',
    'Grade', 'School', 'Province', 'District', 'Circuit',
    'Teacher', 'Subject', 'GoogleClassroomLink', 'Schedule',
    'ZoomLink', 'ZoomPassword', 'StudentType', 'Gender', 'Reserved'
  ],
  Attendance: [
    'UserId', 'OrganisationId', 'Name', 'Surname', 'Email',
    'School', 'Province', 'District', 'Circuit',
    'Teacher', 'Subject', 'Grade', 'Term', 'Date',
    'Attendance', 'LastModified', 'Activity', 'Gender'
  ],
  Marks: [
    'UserId', 'OrganisationId', 'School', 'Province', 'District',
    'Circuit', 'Teacher', 'Grade', 'Subject', 'Type',
    'Name', 'Term', 'StudentName', 'StudentSurname',
    'Marks', 'AssessmentDate', 'LastModified'
  ],
  Topics: [
    'CreatedDate', 'Teacher', 'Subject', 'Grade',
    'Topic', 'Subtopics', 'Term', 'TermStart', 'TermEnd', 'LastModified'
  ]
};

export const CreateSpreadsheetButton = ({
  organisationName,
  existingIds = {},
  onSpreadsheetCreated,
  disabled = false, // 👈 New prop
}) => {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');

  const createSpreadsheets = async () => {
    if (!organisationName) {
      alert('Please enter Organisation Name first.');
      return;
    }

    const sheetsToCreate = [];
    const sheets = ['Users', 'Attendance', 'Marks', 'Topics'];
    for (const sheet of sheets) {
      const key = sheet.toLowerCase();
      if (!existingIds[key]) {
        sheetsToCreate.push(sheet);
      }
    }

    if (sheetsToCreate.length === 0) {
      alert('All spreadsheets already exist. No action needed.');
      return;
    }

    setLoading(true);
    const newIds = {};

    try {
      for (const sheetName of sheetsToCreate) {
        setProgress(`Creating ${sheetName} sheet...`);
        const key = sheetName.toLowerCase();

        // 1. Create the spreadsheet
        const createResponse = await axios.post(
          'https://sheets.googleapis.com/v4/spreadsheets',
          {
            properties: {
              title: `${organisationName} - ${sheetName}`,
            },
            sheets: [{ properties: { title: sheetName } }],
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const spreadsheetId = createResponse.data.spreadsheetId;
        newIds[key] = spreadsheetId;

        // 2. Add headers (first row)
        const headers = SHEET_HEADERS[sheetName];
        if (headers && headers.length > 0) {
          const range = `${sheetName}!A1:${String.fromCharCode(64 + headers.length)}1`;
          await axios.put(
            `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
            {
              values: [headers],
              majorDimension: 'ROWS',
            },
            {
              params: { valueInputOption: 'USER_ENTERED' },
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            }
          );
        }

        // 3. Set sharing permissions: Anyone with the link can edit
        setProgress(`Sharing ${sheetName}...`);
        try {
          await axios.post(
            `https://www.googleapis.com/drive/v3/files/${spreadsheetId}/permissions`,
            {
              type: 'anyone',
              role: 'writer',
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            }
          );
        } catch (permErr) {
          console.warn(`Failed to set share permissions for ${sheetName}:`, permErr);
          // Continue – we don't want to break the entire flow if sharing fails
        }
      }

      setProgress('Done!');
      const allIds = { ...existingIds, ...newIds };
      if (!allIds.spreadsheetId) {
        allIds.spreadsheetId = allIds.users || '';
      }
      alert(`${sheetsToCreate.length} spreadsheet(s) created with headers and shareable link!`);
      onSpreadsheetCreated(allIds);
    } catch (err) {
      console.error('Failed to create spreadsheets:', err);
      alert('Failed to create one or more spreadsheets. Please check console for details.');
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const missingCount = Object.values(existingIds).filter(id => !id).length;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={createSpreadsheets}
        disabled={loading || missingCount === 0 || disabled} // 👈 Added disabled prop
        className={`bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 text-sm ${
          missingCount === 0 || disabled ? 'cursor-not-allowed' : ''
        }`}
      >
        {loading ? 'Creating...' : `Create Missing (${missingCount})`}
      </button>
      {progress && <span className="text-sm text-gray-600">{progress}</span>}
      {missingCount === 0 && (
        <span className="text-sm text-green-600">✅ All exist</span>
      )}
    </div>
  );
};